import axios from "axios";
import breadcrumbComp from "../../common/breadcrumb-comp.vue";
import EncryptUtility from "../../utility/js/encrypt-utility";
import AddFavourite from "../../utility/js/addFav";
import BackToTop from "../../common/back-to-top.vue";
import Utility from "../../../shared/utility";
import html2pdf from "html2pdf.js";
import { Bar } from "vue-chartjs/legacy";
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const HIDDEN_GROUPED_FIELDS = ["Project", "Business", "Username", "Transaction Date", "Transaction Month"];
const ALL_FILTER_VALUE = "__ALL__";
const TOTAL_TRANSACTIONS = "TotalTransactions";
const FILTER_SEQUENCE = ["Project", "Business", "Username", "TransactionType", "TransactionMonth"];
const DATE_FILTER_SEQUENCE = ["Project", "Business", "Username", "TransactionType", "TransactionDate"];
const REPORT_NAME = "productivity-analysis";

export default {
  data() {
    return {
      userId: EncryptUtility.localStorageDecrypt("userID"),
      actionList: [],
      favouriteList: [],
      mainPage: "",
      subPage: "",
      backSlash: true,
      pageUrl: "",
      showAdd: false,
      refresh: true,
      selectedTrendDays: null,
      timeRangeList: [
        { text: "Current Day", value: 1 },
        { text: "7 Days", value: 7 },
        { text: "14 Days", value: 14 },
        { text: "30 Days", value: 30 },
        { text: "60 Days", value: 60 },
        { text: "90 Days", value: 90 },
      ],
      excludeMaterialMovement: false,
      filters: {
        Project: [],
        Business: [],
        Username: [],
        TransactionType: [],
        TransactionMonth: [],
      },
      dateFilters: {
        Project: [],
        Business: [],
        Username: [],
        TransactionType: [],
        TransactionDate: [],
      },
      filterOptions: {
        Project: [],
        Business: [],
        Username: [],
        TransactionType: [],
        TransactionMonth: [],
        TransactionDate: [],
      },
      reportData: [],
      scalarColumns: [],
      hourColumns: [],
      hourColumnNames: [],
      expandedSummaryRows: [],
      expandedDateSummaryRows: [],
      selectedHourlyChartPoint: null,
      savedReportFilters: null,
      suppressFilterSave: false,
    };
  },
  async created() {
    this.route = this.$route.path.replace("/", "");
    let data = await AddFavourite.getPageAction(this.userId, this.route);
    this.actionList = data.actionList;
    this.mainPage = data.mainPage;
    this.favouriteList = data.favouriteList;
    this.pageUrl = data.pageUrl;
    this.subPage = data.subPage;
    this.showAdd = data.showAdd;
    await this.loadSavedReportFilters();
    if (this.selectedTrendDays) await this.runReport(false);
  },
  computed: {
    hasReportData() {
      return this.reportData.length > 0;
    },
    analysisRows() {
      if (!this.excludeMaterialMovement) return this.reportData;
      return this.reportData.filter((row) => {
        return !this.includesMaterialMovement(row["Transaction Type"]);
      });
    },
    reportHeaders() {
      const scalarHeaders = this.scalarColumns.map((column) => ({
        text: column,
        value: column,
        align: this.isIntegerColumn(column) ? "center" : "start",
        class: this.isIntegerColumn(column) ? "primary customwhite--text text-center" : "primary customwhite--text",
        cellClass: this.isIntegerColumn(column) ? "text-center" : "",
      }));
      const hourHeaders = this.hourColumns.map((column) => ({
        text: column.text,
        value: column.value,
        align: "center",
        class: "primary customwhite--text text-center",
        cellClass: "text-center",
      }));
      return [...scalarHeaders, ...hourHeaders];
    },
    detailHeaders() {
      const detailScalarHeaders = ["Project", "Business", "Username"].map((column) => ({
        text: column,
        value: column,
        align: "start",
        class: "primary customwhite--text",
      }));
      const detailMetricHeaders = [
        {
          text: TOTAL_TRANSACTIONS,
          value: TOTAL_TRANSACTIONS,
          align: "center",
          class: "primary customwhite--text text-center",
          cellClass: "text-center",
        },
        ...this.hourColumns.map((column) => ({
          text: column.text,
          value: column.value,
          align: "center",
          class: "primary customwhite--text text-center",
          cellClass: "text-center",
        })),
      ];
      return [...detailScalarHeaders, ...detailMetricHeaders];
    },
    hourlyChartData() {
      return {
        labels: this.hourColumns.map((column) => column.text),
        datasets: this.groupedRows.map((row, index) => ({
          label: row["Transaction Type"] || "Unknown",
          data: this.hourColumns.map((column) => this.toNumber(row[column.value])),
          backgroundColor: this.chartColor(index),
          borderColor: this.chartColor(index),
          borderWidth: 1,
          stack: "transactions",
        })),
      };
    },
    hourlyChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements, chart) => this.onHourlyChartClick(elements, chart),
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxHeight: 10,
              boxWidth: 18,
              color: this.$vuetify.theme.dark ? "#ffffff" : "#263238",
            },
          },
          title: {
            display: false,
          },
          tooltip: {
            mode: "index",
            intersect: false,
          },
        },
        interaction: {
          intersect: true,
          mode: "nearest",
        },
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false,
            },
            ticks: {
              color: this.$vuetify.theme.dark ? "#ffffff" : "#263238",
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              precision: 0,
              color: this.$vuetify.theme.dark ? "#ffffff" : "#263238",
            },
          },
        },
      };
    },
    hourlyRawHeaders() {
      const hourLabel = this.selectedHourlyChartPoint?.hourLabel || "Hour";
      return [
        { text: "Project", value: "Project", class: "primary customwhite--text" },
        { text: "Business", value: "Business", class: "primary customwhite--text" },
        { text: "Username", value: "Username", class: "primary customwhite--text" },
        { text: "Transaction Date", value: "Transaction Date", class: "primary customwhite--text" },
        { text: "Transaction Month", value: "Transaction Month", class: "primary customwhite--text" },
        { text: `${hourLabel} Transactions`, value: "HourTransactions", align: "center", class: "primary customwhite--text text-center", cellClass: "text-center" },
        { text: TOTAL_TRANSACTIONS, value: TOTAL_TRANSACTIONS, align: "center", class: "primary customwhite--text text-center", cellClass: "text-center" },
      ];
    },
    hourlyRawRows() {
      if (!this.selectedHourlyChartPoint) return [];
      const { transactionType, hourColumn } = this.selectedHourlyChartPoint;

      return this.filteredRows
        .filter((row) => row["Transaction Type"] === transactionType && this.toNumber(row[hourColumn]) > 0)
        .map((row) => ({
          Project: row.Project || "",
          Business: row.Business || "",
          Username: row.Username || "",
          "Transaction Date": row["Transaction Date"] || "",
          "Transaction Month": row["Transaction Month"] || "",
          HourTransactions: this.toNumber(row[hourColumn]),
          [TOTAL_TRANSACTIONS]: this.toNumber(row[TOTAL_TRANSACTIONS]),
        }));
    },
    rawPrintHeaders() {
      const baseHeaders = ["Project", "Business", "Username", "Transaction Type", "Transaction Date", "Transaction Month"];
      const metricHeaders = [TOTAL_TRANSACTIONS, ...this.hourColumns.map((column) => column.value)];
      return [...baseHeaders, ...metricHeaders].filter((column, index, columns) => columns.indexOf(column) === index);
    },
    printParameterSummary() {
      return [
        { label: "Time Range", value: this.selectedTrendDays ? `${this.selectedTrendDays} Days` : "" },
        { label: "Project", value: this.printSelectedValues(this.filters.Project) },
        { label: "Business", value: this.printSelectedValues(this.filters.Business) },
        { label: "Username", value: this.printSelectedValues(this.filters.Username) },
        { label: "Transaction Type", value: this.printSelectedValues(this.filters.TransactionType) },
        { label: "Transaction Month", value: this.printSelectedValues(this.filters.TransactionMonth) },
        { label: "Transaction Date Summary Date", value: this.printSelectedValues(this.dateFilters.TransactionDate) },
        { label: "Exclude Material Movement", value: this.excludeMaterialMovement ? "Yes" : "No" },
      ];
    },
    filteredRows() {
      return this.analysisRows.filter((row) => {
        return (
          this.matchesFilter(row.Project, this.filters.Project) &&
          this.matchesFilter(row.Business, this.filters.Business) &&
          this.matchesFilter(row.Username, this.filters.Username) &&
          this.matchesFilter(row["Transaction Type"], this.filters.TransactionType) &&
          this.matchesFilter(row["Transaction Month"], this.filters.TransactionMonth)
        );
      });
    },
    groupedRows() {
      return this.groupRows(this.filteredRows);
    },
    dateFilteredRows() {
      return this.analysisRows.filter((row) => {
        return (
          this.matchesFilter(row.Project, this.dateFilters.Project) &&
          this.matchesFilter(row.Business, this.dateFilters.Business) &&
          this.matchesFilter(row.Username, this.dateFilters.Username) &&
          this.matchesFilter(row["Transaction Type"], this.dateFilters.TransactionType) &&
          this.matchesFilter(row["Transaction Date"], this.dateFilters.TransactionDate)
        );
      });
    },
    dateGroupedRows() {
      return this.groupRows(this.dateFilteredRows);
    },
    projectFilterItems() {
      return this.filterItems("Project", this.filters, FILTER_SEQUENCE);
    },
    businessFilterItems() {
      return this.filterItems("Business", this.filters, FILTER_SEQUENCE);
    },
    usernameFilterItems() {
      return this.filterItems("Username", this.filters, FILTER_SEQUENCE);
    },
    transactionTypeFilterItems() {
      return this.filterItems("TransactionType", this.filters, FILTER_SEQUENCE);
    },
    transactionMonthFilterItems() {
      return this.filterItems("TransactionMonth", this.filters, FILTER_SEQUENCE);
    },
    dateProjectFilterItems() {
      return this.filterItems("Project", this.dateFilters, DATE_FILTER_SEQUENCE);
    },
    dateBusinessFilterItems() {
      return this.filterItems("Business", this.dateFilters, DATE_FILTER_SEQUENCE);
    },
    dateUsernameFilterItems() {
      return this.filterItems("Username", this.dateFilters, DATE_FILTER_SEQUENCE);
    },
    dateTransactionTypeFilterItems() {
      return this.filterItems("TransactionType", this.dateFilters, DATE_FILTER_SEQUENCE);
    },
    transactionDateFilterItems() {
      return this.filterItems("TransactionDate", this.dateFilters, DATE_FILTER_SEQUENCE);
    },
    totalRow() {
      const totals = {};
      this.hourColumns.forEach((column) => {
        totals[column.value] = this.sumColumn(column.value);
      });
      totals[TOTAL_TRANSACTIONS] = this.sumColumn(TOTAL_TRANSACTIONS);
      return totals;
    },
  },
  methods: {
    async resetFunction() {
      this.selectedTrendDays = null;
      this.excludeMaterialMovement = false;
      this.savedReportFilters = null;
      this.filters = {
        Project: [],
        Business: [],
        Username: [],
        TransactionType: [],
        TransactionMonth: [],
      };
      this.dateFilters = {
        Project: [],
        Business: [],
        Username: [],
        TransactionType: [],
        TransactionDate: [],
      };
      this.filterOptions = {
        Project: [],
        Business: [],
        Username: [],
        TransactionType: [],
        TransactionMonth: [],
        TransactionDate: [],
      };
      this.reportData = [];
      this.scalarColumns = [];
      this.hourColumns = [];
      this.hourColumnNames = [];
      this.expandedSummaryRows = [];
      this.expandedDateSummaryRows = [];
      this.selectedHourlyChartPoint = null;
      await this.clearSavedReportFilters();
    },
    async runReport(saveFilters = true) {
      if (!this.selectedTrendDays) return;

      const LoaderDialog = {
        visible: true,
        title: "Please Wait...",
      };
      this.$store.commit("ConfigModule/LoaderDialog", LoaderDialog);

      try {
        const response = await axios.post("rt/productivity_analysis_new", {
          UserId: parseInt(this.userId),
          TrendDays: parseInt(this.selectedTrendDays),
        });
        const body = this.parseMaybeJson(response?.data?.body);
        const status = this.normalizeStatus(body?.status ?? body?.Status);
        const message = body?.message ?? body?.Message ?? response?.data?.message;

        if (!status) {
          this.clearReport();
          this.showAlert("error", this.messageText(message));
          return;
        }

        const reportJson = this.parseMaybeJson(message);
        const rows = this.normalizeRows(reportJson);
        if (rows.length === 0) {
          this.clearReport();
          Utility.showNoDataToast("Data is not available for the specified search criteria.");
          return;
        }

        this.reportData = rows;
        this.setColumns(rows);
        this.setFilterOptions(rows);
        if (this.savedReportFilters) {
          this.applySavedReportFilters(this.savedReportFilters);
          this.savedReportFilters = null;
        } else {
          this.clearFilters();
          this.selectMostRecentTransactionDate();
        }
        if (saveFilters) await this.saveReportFilters();
      } catch (error) {
        this.clearReport();
        this.showAlert("error", Utility.apiError(error));
      } finally {
        LoaderDialog.visible = false;
        this.$store.commit("ConfigModule/LoaderDialog", LoaderDialog);
      }
    },
    normalizeRows(reportJson) {
      const sourceRows = Array.isArray(reportJson)
        ? reportJson
        : Array.isArray(reportJson?.data)
          ? reportJson.data
          : Array.isArray(reportJson?.rows)
            ? reportJson.rows
            : Array.isArray(reportJson?.Resultset)
              ? reportJson.Resultset
              : [];

      this.hourColumnNames = [];
      return sourceRows.map((row) => this.flattenHours(row));
    },
    flattenHours(row) {
      const flattened = {};
      Object.keys(row || {}).forEach((key) => {
        if (key.toLowerCase() !== "hours") {
          flattened[this.displayFieldName(key)] = row[key];
        }
      });

      const hours = row?.hours || row?.Hours;
      if (Array.isArray(hours)) {
        hours.forEach((hour, index) => {
          const hourColumn = this.hourColumnName(hour, index);
          if (!this.hourColumnNames.includes(hourColumn)) this.hourColumnNames.push(hourColumn);
          flattened[hourColumn] = this.hourColumnValue(hour);
        });
      } else if (hours && typeof hours === "object") {
        Object.keys(hours).forEach((key) => {
          if (!this.hourColumnNames.includes(key)) this.hourColumnNames.push(key);
          flattened[key] = hours[key];
        });
      }

      return flattened;
    },
    setColumns(rows) {
      const keys = rows.reduce((columns, row) => {
        Object.keys(row).forEach((key) => {
          if (!columns.includes(key)) columns.push(key);
        });
        return columns;
      }, []);
      const hourColumnValues = keys.filter((key) => this.isHourColumn(key));
      const preferredScalarColumns = ["Transaction Type"].filter((field) => keys.includes(field));
      const otherScalarColumns = keys.filter((key) => (
        !preferredScalarColumns.includes(key) &&
        !HIDDEN_GROUPED_FIELDS.includes(key) &&
        !hourColumnValues.includes(key)
      ));

      this.scalarColumns = [...preferredScalarColumns, ...otherScalarColumns];
      this.hourColumns = hourColumnValues.map((value) => ({ text: value, value }));
    },
    setFilterOptions(rows) {
      this.filterOptions.Project = this.uniqueValues(rows, "Project");
      this.filterOptions.Business = this.uniqueValues(rows, "Business");
      this.filterOptions.Username = this.uniqueValues(rows, "Username");
      this.filterOptions.TransactionType = this.uniqueValues(rows, "Transaction Type");
      this.filterOptions.TransactionMonth = this.uniqueValues(rows, "Transaction Month");
      this.filterOptions.TransactionDate = this.uniqueValues(rows, "Transaction Date");
    },
    clearFilters() {
      this.filters.Project = [];
      this.filters.Business = [];
      this.filters.Username = [];
      this.filters.TransactionType = [];
      this.filters.TransactionMonth = [];
      this.dateFilters.Project = [];
      this.dateFilters.Business = [];
      this.dateFilters.Username = [];
      this.dateFilters.TransactionType = [];
      this.dateFilters.TransactionDate = [];
    },
    clearReport() {
      this.reportData = [];
      this.scalarColumns = [];
      this.hourColumns = [];
      this.hourColumnNames = [];
      this.expandedSummaryRows = [];
      this.expandedDateSummaryRows = [];
      this.selectedHourlyChartPoint = null;
      this.setFilterOptions([]);
      this.clearFilters();
    },
    hourColumnName(hour, index) {
      if (hour && typeof hour === "object") {
        const name = hour.TranType ?? hour.tranType ?? hour.Hour ?? hour.hour ?? hour.Name ?? hour.name ?? hour.Label ?? hour.label ?? hour.Time ?? hour.time;
        if (name !== undefined && name !== null && name !== "") return name.toString();
        const keys = Object.keys(hour);
        if (keys.length === 1) return keys[0];
      }
      return `Hour ${index + 1}`;
    },
    hourColumnValue(hour) {
      if (hour && typeof hour === "object") {
        const value = hour.TotalTransactions ?? hour.totalTransactions ?? hour.Transactions ?? hour.transactions ?? hour.Count ?? hour.count ?? hour.Value ?? hour.value;
        if (value !== undefined && value !== null) return value;
        const keys = Object.keys(hour);
        if (keys.length === 1) return hour[keys[0]];
      }
      return hour;
    },
    isHourColumn(key) {
      return this.hourColumnNames.includes(key);
    },
    isIntegerColumn(column) {
      return column === TOTAL_TRANSACTIONS || this.isHourColumn(column);
    },
    totalValue(column) {
      if (this.hourColumns.some((hour) => hour.value === column) || column === TOTAL_TRANSACTIONS) {
        return this.totalRow[column] || 0;
      }
      return column === this.scalarColumns[0] ? "Totals" : "";
    },
    dailyAverageValue(column) {
      if (this.hourColumns.some((hour) => hour.value === column) || column === TOTAL_TRANSACTIONS) {
        return this.formatAverage((this.totalRow[column] || 0) / this.dailyAverageDays());
      }
      return column === this.scalarColumns[0] ? "Daily Average" : "";
    },
    formatAverage(value) {
      return Number.isInteger(value) ? value : Number(value.toFixed(2));
    },
    dailyAverageDays() {
      const trendDays = parseInt(this.selectedTrendDays) || 1;
      let weekdays = 0;

      for (let dayOffset = 0; dayOffset < trendDays; dayOffset++) {
        const date = new Date();
        date.setDate(date.getDate() - dayOffset);
        const dayOfWeek = date.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) weekdays++;
      }

      return weekdays || 1;
    },
    sumColumn(column) {
      return this.filteredRows.reduce((sum, row) => sum + this.toNumber(row[column]), 0);
    },
    groupRows(rows) {
      const groupedData = {};

      rows.forEach((row) => {
        const transactionType = row["Transaction Type"] || "";
        if (!groupedData[transactionType]) {
          groupedData[transactionType] = {
            "Transaction Type": transactionType,
            [TOTAL_TRANSACTIONS]: 0,
          };
          this.hourColumns.forEach((column) => {
            groupedData[transactionType][column.value] = 0;
          });
        }

        this.hourColumns.forEach((column) => {
          groupedData[transactionType][column.value] += this.toNumber(row[column.value]);
        });
        groupedData[transactionType][TOTAL_TRANSACTIONS] += this.toNumber(row[TOTAL_TRANSACTIONS]);
      });

      return Object.values(groupedData);
    },
    dateTotalValue(column) {
      if (this.hourColumns.some((hour) => hour.value === column) || column === TOTAL_TRANSACTIONS) {
        return this.dateSumColumn(column) || 0;
      }
      return column === this.scalarColumns[0] ? "Totals" : "";
    },
    dateSumColumn(column) {
      return this.dateFilteredRows.reduce((sum, row) => sum + this.toNumber(row[column]), 0);
    },
    matchesFilter(value, selectedValues) {
      return !selectedValues.length || selectedValues.includes(value);
    },
    filterItems(filterName, filterSet = this.filters, sequence = FILTER_SEQUENCE) {
      const values = this.availableFilterValues(filterSet, filterName, sequence);
      return values.length ? [{ text: "All", value: ALL_FILTER_VALUE }, ...values.map((value) => ({ text: value, value }))] : [];
    },
    async onFilterChange(filterName) {
      this.toggleAllFilter(this.filters, filterName, FILTER_SEQUENCE);
      this.pruneDependentFilters(this.filters, filterName, FILTER_SEQUENCE);
      this.syncDateDrilldownFilters(filterName);
      this.closeUnavailableSummaryDrilldown();
      this.clearHourlyChartSelection();
      await this.saveReportFilters();
    },
    async onMaterialMovementToggle() {
      this.pruneUnavailableFilters(this.filters, FILTER_SEQUENCE);
      this.pruneUnavailableFilters(this.dateFilters, DATE_FILTER_SEQUENCE);
      this.selectMostRecentTransactionDate();
      this.closeUnavailableSummaryDrilldown();
      this.closeUnavailableDateSummaryDrilldown();
      this.clearHourlyChartSelection();
      await this.saveReportFilters();
    },
    async onDateFilterChange(filterName) {
      this.toggleAllFilter(this.dateFilters, filterName, DATE_FILTER_SEQUENCE);
      this.pruneDependentFilters(this.dateFilters, filterName, DATE_FILTER_SEQUENCE);
      this.defaultMostRecentTransactionDate(filterName);
      this.closeUnavailableDateSummaryDrilldown();
      await this.saveReportFilters();
    },
    toggleAllFilter(filterSet, filterName, sequence = FILTER_SEQUENCE) {
      const selectedValues = filterSet[filterName] || [];
      const filterValues = this.availableFilterValues(filterSet, filterName, sequence);
      const selectedWithoutAll = selectedValues.filter((value) => value !== ALL_FILTER_VALUE);

      if (selectedValues.includes(ALL_FILTER_VALUE)) {
        filterSet[filterName] = selectedWithoutAll.length === filterValues.length ? [] : filterValues;
      }
    },
    showAllChip(filterName, index) {
      return index === 0 && this.isAllSelected(this.filters, filterName, FILTER_SEQUENCE);
    },
    showDateAllChip(filterName, index) {
      return index === 0 && this.isAllSelected(this.dateFilters, filterName, DATE_FILTER_SEQUENCE);
    },
    isAllSelected(filterSet, filterName, sequence = null) {
      if (typeof filterSet === "string") {
        filterName = filterSet;
        filterSet = this.filters;
      }
      sequence = sequence || (filterSet === this.dateFilters ? DATE_FILTER_SEQUENCE : FILTER_SEQUENCE);
      const selectedValues = filterSet[filterName] || [];
      const filterValues = this.availableFilterValues(filterSet, filterName, sequence);
      return filterValues.length > 0 && selectedValues.length === filterValues.length;
    },
    async clearFilter(filterName) {
      this.filters[filterName] = [];
      this.pruneDependentFilters(this.filters, filterName, FILTER_SEQUENCE);
      this.syncDateDrilldownFilters(filterName);
      this.closeUnavailableSummaryDrilldown();
      this.clearHourlyChartSelection();
      await this.saveReportFilters();
    },
    async clearDateFilter(filterName) {
      this.dateFilters[filterName] = [];
      this.pruneDependentFilters(this.dateFilters, filterName, DATE_FILTER_SEQUENCE);
      this.defaultMostRecentTransactionDate(filterName);
      this.closeUnavailableDateSummaryDrilldown();
      await this.saveReportFilters();
    },
    async removeFilterValue(filterName, value) {
      this.filters[filterName] = (this.filters[filterName] || []).filter((selectedValue) => selectedValue !== value);
      this.pruneDependentFilters(this.filters, filterName, FILTER_SEQUENCE);
      this.syncDateDrilldownFilters(filterName);
      this.closeUnavailableSummaryDrilldown();
      this.clearHourlyChartSelection();
      await this.saveReportFilters();
    },
    syncDateDrilldownFilters(filterName) {
      if (!["Project", "Business"].includes(filterName)) return;
      this.dateFilters.Project = [...(this.filters.Project || [])];
      this.dateFilters.Business = [...(this.filters.Business || [])];
      this.pruneDependentFilters(this.dateFilters, filterName, DATE_FILTER_SEQUENCE);
      this.selectMostRecentTransactionDate();
      this.closeUnavailableDateSummaryDrilldown();
    },
    async removeDateFilterValue(filterName, value) {
      this.dateFilters[filterName] = (this.dateFilters[filterName] || []).filter((selectedValue) => selectedValue !== value);
      this.pruneDependentFilters(this.dateFilters, filterName, DATE_FILTER_SEQUENCE);
      this.defaultMostRecentTransactionDate(filterName);
      this.closeUnavailableDateSummaryDrilldown();
      await this.saveReportFilters();
    },
    defaultMostRecentTransactionDate(filterName) {
      if (filterName !== "TransactionDate") this.selectMostRecentTransactionDate();
    },
    onHourlyChartClick(elements, chart) {
      if (!elements.length) {
        this.clearHourlyChartSelection();
        return;
      }

      const chartElement = elements[0];
      const dataset = chart.data.datasets[chartElement.datasetIndex];
      const hourColumn = this.hourColumns[chartElement.index];
      if (!dataset || !hourColumn) return;

      this.selectedHourlyChartPoint = {
        transactionType: dataset.label,
        hourColumn: hourColumn.value,
        hourLabel: hourColumn.text,
      };
    },
    clearHourlyChartSelection() {
      this.selectedHourlyChartPoint = null;
    },
    hourlyRawSubtotalValue(column) {
      if (column === "Project") return "Subtotal";
      if (["Business", "Username", "Transaction Date", "Transaction Month"].includes(column)) return "";
      return this.hourlyRawRows.reduce((sum, row) => sum + this.toNumber(row[column]), 0);
    },
    toggleSummaryRow(item) {
      const transactionType = item?.["Transaction Type"];
      const expandedTransactionType = this.expandedSummaryRows[0]?.["Transaction Type"];
      this.expandedSummaryRows = expandedTransactionType === transactionType ? [] : [item];
    },
    toggleDateSummaryRow(item) {
      const transactionType = item?.["Transaction Type"];
      const expandedTransactionType = this.expandedDateSummaryRows[0]?.["Transaction Type"];
      this.expandedDateSummaryRows = expandedTransactionType === transactionType ? [] : [item];
    },
    transactionTypeDetailRows(item, sourceRows = this.filteredRows) {
      const transactionType = item?.["Transaction Type"];
      const groupedDetails = {};

      sourceRows
        .filter((row) => row["Transaction Type"] === transactionType)
        .forEach((row) => {
          const project = row.Project || "";
          const business = row.Business || "";
          const username = row.Username || "";
          const detailKey = [project, business, username].join("||");

          if (!groupedDetails[detailKey]) {
            groupedDetails[detailKey] = {
              Project: project,
              Business: business,
              Username: username,
              [TOTAL_TRANSACTIONS]: 0,
            };
            this.hourColumns.forEach((column) => {
              groupedDetails[detailKey][column.value] = 0;
            });
          }

          groupedDetails[detailKey][TOTAL_TRANSACTIONS] += this.toNumber(row[TOTAL_TRANSACTIONS]);
          this.hourColumns.forEach((column) => {
            groupedDetails[detailKey][column.value] += this.toNumber(row[column.value]);
          });
        });

      return Object.values(groupedDetails);
    },
    detailSubtotalValue(item, column, sourceRows = this.filteredRows) {
      if (column === "Project") return "Subtotal";
      if (["Business", "Username"].includes(column)) return "";
      return this.transactionTypeDetailRows(item, sourceRows).reduce((sum, row) => sum + this.toNumber(row[column]), 0);
    },
    closeUnavailableSummaryDrilldown() {
      if (!this.expandedSummaryRows.length) return;
      const expandedTransactionType = this.expandedSummaryRows[0]?.["Transaction Type"];
      const matchingRow = this.groupedRows.find((row) => row["Transaction Type"] === expandedTransactionType);
      this.expandedSummaryRows = matchingRow ? [matchingRow] : [];
    },
    closeUnavailableDateSummaryDrilldown() {
      if (!this.expandedDateSummaryRows.length) return;
      const expandedTransactionType = this.expandedDateSummaryRows[0]?.["Transaction Type"];
      const matchingRow = this.dateGroupedRows.find((row) => row["Transaction Type"] === expandedTransactionType);
      this.expandedDateSummaryRows = matchingRow ? [matchingRow] : [];
    },
    pruneDependentFilters(filterSet, filterName, sequence) {
      const changedIndex = sequence.indexOf(filterName);
      if (changedIndex === -1) return;

      sequence.slice(changedIndex + 1).forEach((dependentFilter) => {
        const availableValues = this.availableFilterValues(filterSet, dependentFilter, sequence);
        filterSet[dependentFilter] = (filterSet[dependentFilter] || []).filter((value) => availableValues.includes(value));
      });
    },
    pruneUnavailableFilters(filterSet, sequence) {
      sequence.forEach((filterName) => {
        const availableValues = this.availableFilterValues(filterSet, filterName, sequence);
        filterSet[filterName] = (filterSet[filterName] || []).filter((value) => availableValues.includes(value));
      });
    },
    availableFilterValues(filterSet, filterName, sequence) {
      const filterIndex = sequence.indexOf(filterName);
      const parentFilters = filterIndex > 0 ? sequence.slice(0, filterIndex) : [];
      const rows = this.analysisRows.filter((row) => {
        return parentFilters.every((parentFilter) => {
          const selectedValues = filterSet[parentFilter] || [];
          return this.matchesFilter(row[this.dataFieldName(parentFilter)], selectedValues);
        });
      });
      return this.uniqueValues(rows, this.dataFieldName(filterName));
    },
    includesMaterialMovement(value) {
      return (value || "").toString().toLowerCase().includes("material movement");
    },
    selectMostRecentTransactionDate() {
      const latestDate = this.availableFilterValues(this.dateFilters, "TransactionDate", DATE_FILTER_SEQUENCE)
        .slice()
        .sort((firstDate, secondDate) => new Date(secondDate) - new Date(firstDate))[0];
      this.dateFilters.TransactionDate = latestDate ? [latestDate] : [];
    },
    uniqueValues(rows, key) {
      return [...new Set(rows.map((row) => row[key]).filter((value) => value !== undefined && value !== null && value !== ""))];
    },
    displayFieldName(key) {
      const normalizedKey = key.toString().toLowerCase().replace(/[^a-z0-9]/g, "");
      if (normalizedKey === "transactiontype" || normalizedKey === "trantype") return "Transaction Type";
      if (normalizedKey === "transactiondate" || normalizedKey === "trandate") return "Transaction Date";
      if (normalizedKey === "transactionmonth" || normalizedKey === "tranmonth") return "Transaction Month";
      if (normalizedKey === "username") return "Username";
      if (normalizedKey === "project") return "Project";
      if (normalizedKey === "business") return "Business";
      return key;
    },
    dataFieldName(filterName) {
      const fieldMap = {
        Project: "Project",
        Business: "Business",
        Username: "Username",
        TransactionType: "Transaction Type",
        TransactionMonth: "Transaction Month",
        TransactionDate: "Transaction Date",
      };
      return fieldMap[filterName] || filterName;
    },
    parseMaybeJson(value) {
      if (typeof value !== "string") return value;
      try {
        return JSON.parse(value);
      } catch (error) {
        return value;
      }
    },
    normalizeStatus(value) {
      return value === true || value === "True" || value === "true" || value === 1 || value === "1";
    },
    messageText(message) {
      return typeof message === "string" ? message : JSON.stringify(message);
    },
    toNumber(value) {
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? 0 : numberValue;
    },
    showAlert(type, message) {
      const Alert = {
        type,
        isShow: true,
        message,
      };
      this.$store.commit("ConfigModule/Alert", Alert);
    },
    addFav() {
      this.showAdd = !this.showAdd;
      AddFavourite.add_remove_Favourite(this.showAdd, this.userId, this.route);
    },
    printCurrentView() {
      window.print();
    },
    async exportRawDataExcel() {
      if (!this.filteredRows.length) {
        Utility.showNoDataToast("Data is not available for the specified search criteria.");
        return;
      }

      const jsonFields = this.rawPrintHeaders.reduce((fields, header) => {
        fields[header] = header;
        return fields;
      }, {});
      await Utility.exportExcel(
        this.filteredRows,
        jsonFields,
        Utility.excelNewFileName("Productivity_Analysis_Raw_Data"),
        "Raw Data",
      );
    },
    exportRawDataPdf() {
      if (!this.filteredRows.length) {
        Utility.showNoDataToast("Data is not available for the specified search criteria.");
        return;
      }

      const headers = this.rawPrintHeaders;
      const tableHeader = headers.map((header) => `<th>${this.escapePrintHtml(header)}</th>`).join("");
      const tableRows = this.filteredRows.map((row) => {
        const cells = headers.map((header) => `<td>${this.escapePrintHtml(row[header])}</td>`).join("");
        return `<tr>${cells}</tr>`;
      }).join("");
      const pdfElement = document.createElement("div");
      pdfElement.innerHTML = `
        <div style="color:#111827;font-family:Arial,sans-serif;">
          <style>
            .raw-data-pdf h1 { font-size: 18px; margin: 0 0 6px; }
            .raw-data-pdf .meta { color: #4b5563; font-size: 11px; margin-bottom: 12px; }
            .raw-data-pdf table { border-collapse: collapse; font-size: 8px; table-layout: fixed; width: 100%; }
            .raw-data-pdf th { background: #005ba8; color: #ffffff; text-align: left; }
            .raw-data-pdf th, .raw-data-pdf td { border: 1px solid #d1d5db; overflow-wrap: anywhere; padding: 3px 4px; }
            .raw-data-pdf tbody tr:nth-child(even) { background: #f9fafb; }
          </style>
          <div class="raw-data-pdf">
          <h1>Productivity Analysis Raw Data</h1>
          <div class="meta">${this.escapePrintHtml(this.printFilterSummary())}</div>
          <table>
            <thead><tr>${tableHeader}</tr></thead>
            <tbody>${tableRows}</tbody>
          </table>
          </div>
        </div>
      `;

      html2pdf(pdfElement, {
        margin: 0.2,
        filename: Utility.excelFileName("Productivity_Analysis_Raw_Data").replace(".xls", ".pdf"),
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "a0", orientation: "landscape" },
      });
    },
    printFilterSummary() {
      return [
        `Time Range: ${this.selectedTrendDays || ""} Days`,
        `Project: ${this.printSelectedValues(this.filters.Project)}`,
        `Business: ${this.printSelectedValues(this.filters.Business)}`,
        `Username: ${this.printSelectedValues(this.filters.Username)}`,
        `Transaction Type: ${this.printSelectedValues(this.filters.TransactionType)}`,
        `Transaction Month: ${this.printSelectedValues(this.filters.TransactionMonth)}`,
        `Exclude Material Movement: ${this.excludeMaterialMovement ? "Yes" : "No"}`,
      ].join(" | ");
    },
    printSelectedValues(values) {
      return values?.length ? values.join(", ") : "All";
    },
    escapePrintHtml(value) {
      return (value === undefined || value === null ? "" : value.toString())
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    async loadSavedReportFilters() {
      try {
        const response = await axios.post("rt/report_filter_save", {
          UserId: parseInt(this.userId),
          Pass: 1,
          report_name: REPORT_NAME,
          Json: "",
        });
        const savedFilters = this.parseReportFilterResponse(response?.data);
        const parsedFilters = this.parseSavedReportFilters(savedFilters);
        if (!parsedFilters) return;

        this.savedReportFilters = parsedFilters;
        this.selectedTrendDays = parsedFilters.selectedTrendDays;
      } catch (error) {
        this.savedReportFilters = null;
      }
    },
    async saveReportFilters() {
      if (this.suppressFilterSave || !this.selectedTrendDays) return;

      const jsonPayload = JSON.stringify({
        selectedTrendDays: this.selectedTrendDays,
        excludeMaterialMovement: this.excludeMaterialMovement,
        filters: this.filters,
        dateFilters: this.dateFilters,
      });

      try {
        await axios.post("rt/report_filter_save", {
          UserId: parseInt(this.userId),
          Pass: 2,
          report_name: REPORT_NAME,
          Json: jsonPayload,
        });
      } catch (error) {
        this.showAlert("error", Utility.apiError(error));
      }
    },
    async clearSavedReportFilters() {
      try {
        await axios.post("rt/report_filter_save", {
          UserId: parseInt(this.userId),
          Pass: 2,
          report_name: REPORT_NAME,
          Json: "",
        });
      } catch (error) {
        this.showAlert("error", Utility.apiError(error));
      }
    },
    parseReportFilterResponse(responseData) {
      const body = this.parseMaybeJson(responseData?.body ?? responseData);
      if (!body || body?.message === "NA" || body?.Message === "NA") return null;

      const jsonValue = body?.Json ?? body?.json ?? body?.JSON ?? body?.filter_json ?? body?.FilterJson;
      if (jsonValue !== undefined && jsonValue !== null && jsonValue !== "") return this.parseMaybeJson(jsonValue);

      const message = body?.message ?? body?.Message;
      if (message !== undefined && message !== null && message !== "") return this.parseMaybeJson(message);

      return body;
    },
    parseSavedReportFilters(savedFilters) {
      if (!savedFilters || savedFilters?.message === "NA" || savedFilters?.Message === "NA") return null;

      let source = this.parseMaybeJson(savedFilters);
      const jsonValue = source?.Json ?? source?.json ?? source?.JSON ?? source?.filter_json ?? source?.FilterJson;
      if (jsonValue !== undefined && jsonValue !== null && jsonValue !== "") source = this.parseMaybeJson(jsonValue);
      if (!source || typeof source !== "object") return null;

      return {
        selectedTrendDays: this.normalizeTrendDays(source.selectedTrendDays ?? source.trendDays ?? source.TrendDays),
        excludeMaterialMovement: source.excludeMaterialMovement === true || source.excludeMaterialMovement === "true",
        filters: this.normalizeSavedFilterSet(source.filters, FILTER_SEQUENCE),
        dateFilters: this.normalizeSavedFilterSet(source.dateFilters, DATE_FILTER_SEQUENCE),
      };
    },
    applySavedReportFilters(savedFilters) {
      this.suppressFilterSave = true;
      try {
        this.excludeMaterialMovement = savedFilters.excludeMaterialMovement;
        this.filters = savedFilters.filters;
        this.dateFilters = savedFilters.dateFilters;
        this.pruneUnavailableFilters(this.filters, FILTER_SEQUENCE);
        this.pruneUnavailableFilters(this.dateFilters, DATE_FILTER_SEQUENCE);
        if (!this.dateFilters.TransactionDate.length) this.selectMostRecentTransactionDate();
        this.closeUnavailableSummaryDrilldown();
        this.closeUnavailableDateSummaryDrilldown();
        this.clearHourlyChartSelection();
      } finally {
        this.suppressFilterSave = false;
      }
    },
    normalizeTrendDays(value) {
      const trendDays = Array.isArray(value) ? value[0] : value;
      const numericTrendDays = parseInt(trendDays);
      return Number.isInteger(numericTrendDays) ? numericTrendDays : null;
    },
    normalizeSavedFilterSet(filterSet, sequence) {
      return sequence.reduce((filters, filterName) => {
        filters[filterName] = this.toSavedFilterArray(filterSet?.[filterName]);
        return filters;
      }, {});
    },
    toSavedFilterArray(value) {
      if (value === undefined || value === null || value === "") return [];
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        try {
          const parsedValue = JSON.parse(value);
          return Array.isArray(parsedValue) ? parsedValue : [parsedValue];
        } catch (error) {
          return value.split(",").map((item) => item.trim()).filter(Boolean);
        }
      }
      return [value];
    },
    actionSetter(action) {
      if (this.actionList !== undefined && this.actionList !== null && this.actionList !== "") {
        let filter = this.actionList.filter((entry) => entry.SystemName === action);
        return filter !== undefined && filter !== null && filter.length == 1 ? true : false;
      }
      return false;
    },
    chartColor(index) {
      const colors = [
        "#005BA8",
        "#38A169",
        "#D69E2E",
        "#805AD5",
        "#DD6B20",
        "#319795",
        "#E53E3E",
        "#4A5568",
        "#2B6CB0",
        "#718096",
      ];
      return colors[index % colors.length];
    },
  },
  components: {
    breadcrumbComp,
    BackToTop,
    Bar,
  },
};
